import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Palette, Upload, Save, X, Loader2, AlertCircle, ExternalLink } from 'lucide-react';
import { useBranding } from '../contexts/BrandingContext';

/**
 * Org branding manager modal. Loads + edits white-label fields for one org.
 *
 * Endpoints used (added in branding.routes.cjs):
 *   GET  /api/platform/organizations/:id/branding
 *   PUT  /api/platform/organizations/:id/branding
 *   POST /api/platform/organizations/:id/branding/logo
 *   POST /api/platform/organizations/:id/branding/favicon
 *
 * Authz is enforced server-side: platform admin OR org admin of this org.
 */

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

const OrgBrandingManager = ({ orgId, orgName, onClose }) => {
  const { refreshBranding } = useBranding();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null); // 'logo' | 'favicon' | null
  const [error, setError] = useState(null);
  const [showCss, setShowCss] = useState(false);
  const [form, setForm] = useState({
    brandName: '',
    brandLogoUrl: '',
    brandFaviconUrl: '',
    brandPrimaryColor: '#3b82f6',
    brandAccentColor: '#10b981',
    brandLoginBg: '',
    brandCssOverrides: '',
    poweredByVisible: true,
    customDomain: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/platform/organizations/${orgId}/branding`, { credentials: 'include' });
      if (!res.ok) throw new Error(`Load failed (${res.status})`);
      const data = await res.json();
      const b = data.branding || {};
      setForm(prev => ({
        ...prev,
        brandName: b.brandName || '',
        brandLogoUrl: b.brandLogoUrl || '',
        brandFaviconUrl: b.brandFaviconUrl || '',
        brandPrimaryColor: b.brandPrimaryColor || '#3b82f6',
        brandAccentColor: b.brandAccentColor || '#10b981',
        brandLoginBg: b.brandLoginBg || '',
        brandCssOverrides: b.brandCssOverrides || '',
        poweredByVisible: b.poweredByVisible !== false,
        customDomain: b.customDomain || '',
      }));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setError(null);
    if (form.brandPrimaryColor && !HEX_RE.test(form.brandPrimaryColor)) {
      setError('Primary color must be a 6-digit hex (#rrggbb)');
      return;
    }
    if (form.brandAccentColor && !HEX_RE.test(form.brandAccentColor)) {
      setError('Accent color must be a 6-digit hex (#rrggbb)');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`/api/platform/organizations/${orgId}/branding`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Save failed (${res.status})`);
      setForm(prev => ({ ...prev, ...data.branding }));
      toast.success('Branding saved');
      await refreshBranding(orgId);
    } catch (e) {
      setError(e.message);
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (kind, file) => {
    if (!file) return;
    setError(null);
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`/api/platform/organizations/${orgId}/branding/${kind}`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Upload failed (${res.status})`);
      const field = kind === 'logo' ? 'brandLogoUrl' : 'brandFaviconUrl';
      setForm(prev => ({ ...prev, [field]: data.url }));
      await refreshBranding(orgId);
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(null);
    }
  };

  return (
    <div className="platform-modal-overlay" onClick={onClose}>
      <div className="platform-modal branding-modal" onClick={e => e.stopPropagation()}>
        <div className="platform-modal-header">
          <h3><Palette size={18} /> Branding — {orgName}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>

        <div className="platform-modal-body">
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <Loader2 size={24} className="spin" />
            </div>
          ) : (
            <>
              {error && (
                <div className="branding-error">
                  <AlertCircle size={14} /> {error}
                </div>
              )}

              <div className="platform-form-group">
                <label>Company / Brand Name</label>
                <input
                  type="text"
                  value={form.brandName}
                  onChange={e => update('brandName', e.target.value)}
                  placeholder="e.g. Partner Security"
                />
              </div>

              <div className="platform-form-group">
                <label>Logo</label>
                <p className="platform-form-hint">
                  Recommended: a square or wide image (roughly 300x300px or
                  larger), PNG with a transparent background works best. It
                  will display at about 72px tall in the sidebar.
                </p>
                {form.brandLogoUrl && (
                  <div className="branding-preview">
                    <img src={form.brandLogoUrl} alt="Logo preview" className="branding-logo-preview" />
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', wordBreak: 'break-all' }}>
                      {form.brandLogoUrl}
                    </div>
                  </div>
                )}
                <label className="btn btn-secondary" style={{ marginTop: 8, cursor: 'pointer' }}>
                  {uploading === 'logo' ? <><Loader2 size={14} className="spin" /> Uploading...</> : <><Upload size={14} /> Upload Logo</>}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    onChange={e => handleUpload('logo', e.target.files?.[0])}
                    style={{ display: 'none' }}
                    disabled={!!uploading}
                  />
                </label>
              </div>

              <div className="platform-form-group">
                <label>Favicon</label>
                {form.brandFaviconUrl && (
                  <div className="branding-preview">
                    <img src={form.brandFaviconUrl} alt="Favicon preview" style={{ width: 32, height: 32 }} />
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', wordBreak: 'break-all', marginTop: 8 }}>
                      {form.brandFaviconUrl}
                    </div>
                  </div>
                )}
                <label className="btn btn-secondary" style={{ marginTop: 8, cursor: 'pointer' }}>
                  {uploading === 'favicon' ? <><Loader2 size={14} className="spin" /> Uploading...</> : <><Upload size={14} /> Upload Favicon</>}
                  <input
                    type="file"
                    accept="image/x-icon,image/png,image/svg+xml,.ico"
                    onChange={e => handleUpload('favicon', e.target.files?.[0])}
                    style={{ display: 'none' }}
                    disabled={!!uploading}
                  />
                </label>
              </div>

              <div className="platform-form-group">
                <label>Primary Color</label>
                <div className="branding-color-row">
                  <input
                    type="color"
                    value={HEX_RE.test(form.brandPrimaryColor) ? form.brandPrimaryColor : '#3b82f6'}
                    onChange={e => update('brandPrimaryColor', e.target.value)}
                  />
                  <input
                    type="text"
                    value={form.brandPrimaryColor}
                    onChange={e => update('brandPrimaryColor', e.target.value)}
                    placeholder="#3b82f6"
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              <div className="platform-form-group">
                <label>Accent Color</label>
                <div className="branding-color-row">
                  <input
                    type="color"
                    value={HEX_RE.test(form.brandAccentColor) ? form.brandAccentColor : '#10b981'}
                    onChange={e => update('brandAccentColor', e.target.value)}
                  />
                  <input
                    type="text"
                    value={form.brandAccentColor}
                    onChange={e => update('brandAccentColor', e.target.value)}
                    placeholder="#10b981"
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              <div className="platform-form-group">
                <label>Login Background (CSS color or gradient)</label>
                <input
                  type="text"
                  value={form.brandLoginBg}
                  onChange={e => update('brandLoginBg', e.target.value)}
                  placeholder="e.g. #0f172a or linear-gradient(135deg, #1e3a8a, #0f172a)"
                />
              </div>

              <div className="platform-form-group">
                <label>Custom Domain</label>
                <input
                  type="text"
                  value={form.customDomain}
                  onChange={e => update('customDomain', e.target.value.toLowerCase())}
                  placeholder="app.partner.com"
                />
                <p className="platform-form-hint">
                  Point a CNAME from your domain to <code>app.serverliccm.com</code>.
                  The white-label kicks in once DNS resolves and the request arrives with this Host header.
                </p>
              </div>

              <div className="platform-form-group">
                <label className="branding-toggle-row">
                  <span>Show "Powered by Serverli" footer</span>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={form.poweredByVisible}
                      onChange={e => update('poweredByVisible', e.target.checked)}
                    />
                    <span className="toggle-slider" />
                  </label>
                </label>
              </div>

              <div className="platform-form-group">
                <button
                  type="button"
                  className="btn btn-secondary btn-small"
                  onClick={() => setShowCss(s => !s)}
                  style={{ marginBottom: 8 }}
                >
                  {showCss ? 'Hide' : 'Show'} Custom CSS Overrides
                </button>
                {showCss && (
                  <textarea
                    value={form.brandCssOverrides}
                    onChange={e => update('brandCssOverrides', e.target.value)}
                    placeholder="/* Inline CSS that overrides app styles */"
                    rows={8}
                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                  />
                )}
              </div>
            </>
          )}
        </div>

        <div className="platform-modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading || saving}>
            {saving ? <><Loader2 size={14} className="spin" /> Saving...</> : <><Save size={14} /> Save Branding</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrgBrandingManager;
